import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody, Col, Container, Label, Row, Button, Input } from "reactstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getClinic, getClinicSchedule, saveClinicSchedule } from "service/api";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];

const currentYear = new Date().getFullYear();

const monthOptions = MONTH_NAMES.map((name, i) => ({
    value: i + 1,
    label: `${name} ${currentYear}`,
}));

// "HH:MM" → total minutes
const toMins = (t) => {
    const [h, m] = t.split(":").map(Number);
    return h * 60 + m;
};

// total minutes → "HH:MM"
const toTime = (mins) =>
    `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

// "08:00" → "08:00 AM", "13:30" → "01:30 PM"
const formatTime12 = (t) => {
    if (!t) return "";
    const [h, m] = t.split(":").map(Number);
    const ampm = h >= 12 ? "PM" : "AM";
    const hour = h % 12 || 12;
    return `${String(hour).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
};

const buildTimeSlots = (startTime, endTime, durationMins, slotsPerBlock = 1) => {
    const slots = [];
    let current = toMins(startTime);
    const end = toMins(endTime);
    while (current + durationMins <= end) {
        const next = current + durationMins;
        slots.push({
            start_time: toTime(current),
            end_time: toTime(next),
            slots: slotsPerBlock,
            booked: 0,
        });
        current = next;
    }
    return slots;
};

const ClinicSchedule = () => {
    const { clinicId } = useParams();

    const [clinics, setClinics] = useState([]);
    const [selectedClinic, setSelectedClinic] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [scheduleExists, setScheduleExists] = useState(false);
    const [days, setDays] = useState([]);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [dirtyRows, setDirtyRows] = useState(new Set()); // rows needing slot regen
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    document.title = "Clinic Schedule";

    useEffect(() => { fetchClinics(); }, []);

    const fetchClinics = async () => {
        try {
            const res = await getClinic();
            const list = res?.data || [];
            setClinics(list);
            if (clinicId) {
                const found = list.find(c => c._id === clinicId);
                if (found) setSelectedClinic({ value: found._id, label: found.name });
            }
        } catch (err) {
            console.error("Error fetching clinics", err);
        }
    };

    useEffect(() => {
        if (selectedClinic && selectedMonth) {
            fetchSchedule();
        } else {
            setDays([]);
        }
    }, [selectedClinic, selectedMonth]);

    const fetchSchedule = async () => {
        setLoading(true);
        setExpandedRows(new Set());
        setDirtyRows(new Set());
        try {
            const res = await getClinicSchedule(selectedClinic.value, currentYear, selectedMonth.value);
            if (res?.data) {
                setScheduleExists(res.data.exists || false);
                setDays(res.data.days || []);
            }
        } catch (err) {
            console.error("Error fetching schedule", err);
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (idx) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            next.has(idx) ? next.delete(idx) : next.add(idx);
            return next;
        });
    };

    const handleDayChange = (idx, field, value) => {
        setDays(prev =>
            prev.map((day, i) => i === idx ? { ...day, [field]: value } : day)
        );
        // Mark dirty when time/duration fields change so user knows to regenerate
        if (["start_time", "end_time", "slot_duration", "slots_per_block"].includes(field)) {
            setDirtyRows(prev => new Set(prev).add(idx));
        }
    };

    const regenerateSlots = (idx) => {
        setDays(prev =>
            prev.map((day, i) => {
                if (i !== idx) return day;
                const time_slots = buildTimeSlots(
                    day.start_time, day.end_time,
                    day.slot_duration, day.slots_per_block
                );
                return { ...day, time_slots };
            })
        );
        setDirtyRows(prev => { const n = new Set(prev); n.delete(idx); return n; });
        // Auto-expand so user sees the new slots
        setExpandedRows(prev => new Set(prev).add(idx));
    };

    const handleTimeSlotChange = (dayIdx, slotIdx, value) => {
        setDays(prev =>
            prev.map((day, i) => {
                if (i !== dayIdx) return day;
                const time_slots = day.time_slots.map((ts, si) => {
                    if (si !== slotIdx) return ts;
                    // Booked slots are fully locked — UI won't call this but guard anyway
                    if ((ts.booked || 0) > 0) return ts;
                    return { ...ts, slots: Math.max(parseInt(value) || 1, 1) };
                });
                return { ...day, time_slots };
            })
        );
    };

    const handleSave = async () => {
        if (!selectedClinic || !selectedMonth) {
            Swal.fire("Warning", "Please select clinic and month first.", "warning");
            return;
        }
        setSaving(true);
        try {
            await saveClinicSchedule({
                clinic: selectedClinic.value,
                year: currentYear,
                month: selectedMonth.value,
                days,
            });
            Swal.fire({
                icon: "success",
                title: "Saved!",
                text: "Schedule saved successfully.",
                timer: 1500,
                showConfirmButton: false,
            });
            setScheduleExists(true);
            setDirtyRows(new Set());
        } catch (err) {
            Swal.fire("Error", "Failed to save schedule.", "error");
        } finally {
            setSaving(false);
        }
    };

    const getFullDate = (date) => {
        if (!selectedMonth) return date;
        const d = new Date(currentYear, selectedMonth.value - 1, date);
        return d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
    };

    const workingCount = days.filter(d => d.is_working).length;
    const totalSlots = days.reduce((acc, d) => {
        if (!d.is_working) return acc;
        const ts = d.time_slots || [];
        return acc + (ts.length > 0 ? ts.reduce((s, t) => s + (t.slots || 0), 0) : 0);
    }, 0);

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Setting" breadcrumbItem="Clinic Schedule" />
                <Card>
                    <CardBody>
                        <h4 style={{ fontWeight: "bold", color: "#f46a6a", marginBottom: "20px" }}>
                            Monthly Clinic Schedule
                        </h4>

                        {/* Filters */}
                        <Row className="mb-4">
                            <Col md={4}>
                                <Label style={{ fontWeight: "600" }}>Select Clinic</Label>
                                <Select
                                    value={selectedClinic}
                                    onChange={setSelectedClinic}
                                    options={clinics.map(c => ({ value: c._id, label: c.name }))}
                                    placeholder="Choose clinic..."
                                />
                            </Col>
                            <Col md={4}>
                                <Label style={{ fontWeight: "600" }}>Select Month</Label>
                                <Select
                                    value={selectedMonth}
                                    onChange={setSelectedMonth}
                                    options={monthOptions}
                                    placeholder="Choose month..."
                                />
                            </Col>
                            <Col md={4} className="d-flex align-items-end">
                                {days.length > 0 && (
                                    <div style={{ fontSize: "13px" }}>
                                        <span className="badge bg-success me-2">{workingCount} working days</span>
                                        <span className="badge bg-primary me-2">{totalSlots} total slots</span>
                                        {scheduleExists && <span className="badge bg-info">Editing existing</span>}
                                    </div>
                                )}
                            </Col>
                        </Row>

                        {loading && (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status" />
                                <p className="mt-2">Loading schedule...</p>
                            </div>
                        )}

                        {!loading && days.length > 0 && (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-bordered align-middle" style={{ fontSize: "13px" }}>
                                        <thead style={{ backgroundColor: "#f46a6a", color: "#fff" }}>
                                            <tr>
                                                <th style={{ width: "30px" }}></th>
                                                <th>Date</th>
                                                <th style={{ width: "60px" }}>Day</th>
                                                <th style={{ width: "90px", textAlign: "center" }}>Working</th>
                                                <th style={{ width: "100px" }}>Start</th>
                                                <th style={{ width: "100px" }}>End</th>
                                                <th style={{ width: "100px" }}>Duration (min)</th>
                                                <th style={{ width: "90px" }}>Slots/Block</th>
                                                <th style={{ width: "70px", textAlign: "center" }}>Booked</th>
                                                <th style={{ width: "80px", textAlign: "center" }}>Regen</th>
                                                <th>Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {days.map((day, idx) => {
                                                const isExpanded = expandedRows.has(idx);
                                                const isDirty = dirtyRows.has(idx);
                                                const isWeekend = day.day_of_week === "Sat" || day.day_of_week === "Sun";
                                                const rowBg = !day.is_working ? "#fff3f3" : isWeekend ? "#fff8e1" : "#fff";
                                                const timeSlots = day.time_slots || [];
                                                const totalDaySlots = timeSlots.reduce((s, t) => s + (t.slots || 0), 0);
                                                const totalDayBooked = timeSlots.reduce((s, t) => s + (t.booked || 0), 0);

                                                return (
                                                    <React.Fragment key={day.date}>
                                                        {/* Main day row */}
                                                        <tr style={{ backgroundColor: rowBg }}>
                                                            <td style={{ textAlign: "center", cursor: day.is_working ? "pointer" : "default" }}
                                                                onClick={() => day.is_working && toggleExpand(idx)}>
                                                                {day.is_working && (
                                                                    <i className={`mdi mdi-chevron-${isExpanded ? "up" : "down"} text-primary`}
                                                                        style={{ fontSize: "18px" }} />
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div style={{ fontWeight: "600", fontSize: "13px" }}>
                                                                    {getFullDate(day.date)}
                                                                </div>
                                                                {day.is_working && timeSlots.length > 0 && (
                                                                    <small style={{ color: "#888" }}>
                                                                        {timeSlots.length} slots &bull; {totalDaySlots} capacity &bull; {totalDayBooked} booked
                                                                    </small>
                                                                )}
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>
                                                                <span style={{ fontWeight: "600", color: isWeekend ? "#e74c3c" : "#333" }}>
                                                                    {day.day_of_week}
                                                                </span>
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>
                                                                <div className="form-check form-switch d-flex justify-content-center">
                                                                    <input className="form-check-input" type="checkbox"
                                                                        style={{ width: "40px", height: "20px", cursor: "pointer" }}
                                                                        checked={day.is_working}
                                                                        onChange={e => handleDayChange(idx, "is_working", e.target.checked)} />
                                                                </div>
                                                            </td>
                                                            <td>
                                                                <Input type="time" bsSize="sm"
                                                                    value={day.start_time}
                                                                    disabled={!day.is_working}
                                                                    onChange={e => handleDayChange(idx, "start_time", e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <Input type="time" bsSize="sm"
                                                                    value={day.end_time}
                                                                    disabled={!day.is_working}
                                                                    onChange={e => handleDayChange(idx, "end_time", e.target.value)} />
                                                            </td>
                                                            <td>
                                                                <Input type="number" bsSize="sm" min={5}
                                                                    value={day.slot_duration}
                                                                    disabled={!day.is_working}
                                                                    onChange={e => handleDayChange(idx, "slot_duration", parseInt(e.target.value) || 30)} />
                                                            </td>
                                                            <td>
                                                                <Input type="number" bsSize="sm" min={1}
                                                                    value={day.slots_per_block}
                                                                    disabled={!day.is_working}
                                                                    onChange={e => handleDayChange(idx, "slots_per_block", parseInt(e.target.value) || 1)} />
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>
                                                                <span className={`badge ${totalDayBooked > 0 ? "bg-warning" : "bg-secondary"}`}>
                                                                    {totalDayBooked}
                                                                </span>
                                                            </td>
                                                            <td style={{ textAlign: "center" }}>
                                                                {day.is_working && (
                                                                    <button
                                                                        type="button"
                                                                        className={`btn btn-sm ${isDirty ? "btn-warning" : "btn-outline-secondary"}`}
                                                                        title="Regenerate time slots"
                                                                        onClick={() => regenerateSlots(idx)}
                                                                    >
                                                                        <i className="mdi mdi-refresh" />
                                                                    </button>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <Input type="text" bsSize="sm" placeholder="Notes..."
                                                                    value={day.notes}
                                                                    onChange={e => handleDayChange(idx, "notes", e.target.value)} />
                                                            </td>
                                                        </tr>

                                                        {/* Expanded time slots sub-row */}
                                                        {isExpanded && day.is_working && (
                                                            <tr style={{ backgroundColor: "#f0f8ff" }}>
                                                                <td></td>
                                                                <td colSpan={10} style={{ padding: "12px 20px" }}>
                                                                    {timeSlots.length === 0 ? (
                                                                        <div className="text-muted" style={{ fontSize: "13px" }}>
                                                                            No time slots generated yet. Click{" "}
                                                                            <button type="button" className="btn btn-sm btn-warning py-0 px-2"
                                                                                onClick={() => regenerateSlots(idx)}>
                                                                                <i className="mdi mdi-refresh me-1" />Regenerate
                                                                            </button>{" "}to generate.
                                                                        </div>
                                                                    ) : (
                                                                        <table className="table table-sm table-bordered mb-0"
                                                                            style={{ fontSize: "12px", backgroundColor: "#fff" }}>
                                                                            <thead style={{ backgroundColor: "#3f6ad8", color: "#fff" }}>
                                                                                <tr>
                                                                                    <th style={{ width: "40px" }}>#</th>
                                                                                    <th>Time Slot</th>
                                                                                    <th style={{ width: "120px" }}>Slots (capacity)</th>
                                                                                    <th style={{ width: "90px", textAlign: "center" }}>Booked</th>
                                                                                    <th style={{ width: "90px", textAlign: "center" }}>Available</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {timeSlots.map((ts, si) => {
                                                                                    const available = Math.max(0, (ts.slots || 0) - (ts.booked || 0));
                                                                                    return (
                                                                                        <tr key={si}>
                                                                                            <td style={{ color: "#999" }}>{si + 1}</td>
                                                                                            <td style={{ fontWeight: "600" }}>
                                                                                                {formatTime12(ts.start_time)}
                                                                                                <span style={{ color: "#aaa", margin: "0 6px" }}>→</span>
                                                                                                {formatTime12(ts.end_time)}
                                                                                            </td>
                                                                                            <td>
                                                                                                {ts.booked > 0 ? (
                                                                                                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                                                                                                        <div style={{
                                                                                                            width: "80px",
                                                                                                            padding: "3px 8px",
                                                                                                            background: "#f5f5f5",
                                                                                                            border: "1px solid #ddd",
                                                                                                            borderRadius: "4px",
                                                                                                            fontSize: "13px",
                                                                                                            color: "#aaa",
                                                                                                            textAlign: "center",
                                                                                                            filter: "blur(0.5px)",
                                                                                                            userSelect: "none",
                                                                                                            pointerEvents: "none",
                                                                                                        }}>
                                                                                                            {ts.slots}
                                                                                                        </div>
                                                                                                        <i
                                                                                                            className="mdi mdi-lock"
                                                                                                            style={{ color: "#fd7e14", fontSize: "13px" }}
                                                                                                            title={`Locked — ${ts.booked} booking${ts.booked > 1 ? "s" : ""} already made`}
                                                                                                        />
                                                                                                    </div>
                                                                                                ) : (
                                                                                                    <Input
                                                                                                        type="number"
                                                                                                        bsSize="sm"
                                                                                                        min={1}
                                                                                                        value={ts.slots}
                                                                                                        onChange={e => handleTimeSlotChange(idx, si, e.target.value)}
                                                                                                        style={{ maxWidth: "80px" }}
                                                                                                    />
                                                                                                )}
                                                                                            </td>
                                                                                            <td style={{ textAlign: "center" }}>
                                                                                                <span className={`badge ${ts.booked > 0 ? "bg-warning" : "bg-light text-dark"}`}>
                                                                                                    {ts.booked || 0}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td style={{ textAlign: "center" }}>
                                                                                                <span className={`badge ${available > 0 ? "bg-success" : "bg-danger"}`}>
                                                                                                    {available}
                                                                                                </span>
                                                                                            </td>
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {dirtyRows.size > 0 && (
                                    <div className="alert alert-warning py-2 mt-2" style={{ fontSize: "13px" }}>
                                        <i className="mdi mdi-alert-outline me-2" />
                                        {dirtyRows.size} day(s) have changed times/duration.
                                        Click the <strong>refresh</strong> button on those rows to regenerate time slots before saving.
                                    </div>
                                )}

                                <div className="text-end mt-3">
                                    <Button color="success" onClick={handleSave} disabled={saving} style={{ minWidth: "140px" }}>
                                        {saving
                                            ? <><span className="spinner-border spinner-border-sm me-1" />Saving...</>
                                            : <><i className="mdi mdi-content-save me-1" />{scheduleExists ? "Update Schedule" : "Save Schedule"}</>
                                        }
                                    </Button>
                                </div>
                            </>
                        )}

                        {!loading && days.length === 0 && (
                            <div className="text-center py-5 text-muted">
                                <i className="mdi mdi-calendar-month-outline" style={{ fontSize: "48px" }} />
                                <p className="mt-2">Select a clinic and month to load the schedule.</p>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default ClinicSchedule;
